import { useMemo } from 'react'
import { Check, Inbox, Paperclip, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { formatDate } from '@/lib/locale'
import { useAuth } from '@/auth/useAuth'
import { useToast } from '@/hooks/useToast'
import { useConfirmItem, useDismissItem } from '@/features/inbox/queries'
import type { InboxCategory, InboxEmail } from '@/features/inbox/types'

function categoryLabel(email: InboxEmail, categories: InboxCategory[]) {
  if (email.custom_category_id) {
    const match = categories.find((c) => c.id === email.custom_category_id)
    if (match) return match.name
  }
  if (email.system_category) {
    return email.system_category.charAt(0).toUpperCase() + email.system_category.slice(1)
  }
  return 'Unsorted'
}

interface InboxEmailsTableProps {
  emails: InboxEmail[]
  categories: InboxCategory[]
  loading: boolean
  title: string
  hasAccounts: boolean
  onSelectEmail: (emailId: string) => void
  onOpenAttachment: (attachmentId: string, mime: string | null) => void
}

export function InboxEmailsTable({
  emails,
  categories,
  loading,
  title,
  hasAccounts,
  onSelectEmail,
  onOpenAttachment,
}: InboxEmailsTableProps) {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const confirmItem = useConfirmItem()
  const dismissItem = useDismissItem()
  const canManageItems = hasPermission('inbox.items.manage')

  const columns = useMemo(() => {
    const run = (p: Promise<unknown>, ok: string) => {
      p.then(() => toast({ title: ok })).catch((err) => {
        toast({
          title: 'Action failed',
          description: err instanceof Error ? err.message : undefined,
          variant: 'destructive',
        })
      })
    }
    return [
      {
        id: 'from',
        header: 'Sender',
        accessorFn: (e: InboxEmail) => e.from_email || '-',
        cell: ({ row }: { row: { original: InboxEmail } }) => (
          <span className="block max-w-[160px] truncate">{row.original.from_email || '-'}</span>
        ),
      },
      {
        id: 'subject',
        header: 'Subject',
        accessorFn: (e: InboxEmail) => e.subject || '(no subject)',
        cell: ({ row }: { row: { original: InboxEmail } }) => (
          <span className="block max-w-[260px] truncate font-medium">
            {row.original.subject || '(no subject)'}
          </span>
        ),
      },
      {
        id: 'category',
        header: 'Category',
        accessorFn: (e: InboxEmail) => categoryLabel(e, categories),
        cell: ({ row }: { row: { original: InboxEmail } }) => (
          <Badge variant="secondary">{categoryLabel(row.original, categories)}</Badge>
        ),
      },
      {
        id: 'received',
        header: 'Received',
        accessorFn: (e: InboxEmail) => e.internal_date,
        cell: ({ row }: { row: { original: InboxEmail } }) =>
          row.original.internal_date ? formatDate(row.original.internal_date) : '-',
      },
      {
        id: 'attachments',
        header: 'Files',
        accessorFn: (e: InboxEmail) => e.attachments.length,
        cell: ({ row }: { row: { original: InboxEmail } }) => {
          const atts = row.original.attachments
          if (!atts.length) return <span className="text-muted-foreground">-</span>
          return (
            <div className="flex flex-wrap gap-1">
              {atts.map((a) => (
                <Button
                  key={a.id}
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2"
                  title={a.filename || 'attachment'}
                  onClick={(ev) => {
                    ev.stopPropagation()
                    onOpenAttachment(a.id, a.mime_type)
                  }}
                >
                  <Paperclip className="size-3.5" />
                  <span className="max-w-[120px] truncate text-xs">{a.filename || 'file'}</span>
                </Button>
              ))}
            </div>
          )
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }: { row: { original: InboxEmail } }) => {
          const e = row.original
          if (!e.item_id || !canManageItems) return null
          return (
            <div className="flex items-center justify-end gap-1">
              {e.item_status !== 'confirmed' ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  title="Accept"
                  onClick={(ev) => {
                    ev.stopPropagation()
                    run(confirmItem.mutateAsync(e.item_id as string), 'Accepted')
                  }}
                >
                  <Check className="size-4 text-success" />
                </Button>
              ) : null}
              {e.item_status !== 'dismissed' ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-7"
                  title="Dismiss"
                  onClick={(ev) => {
                    ev.stopPropagation()
                    run(dismissItem.mutateAsync(e.item_id as string), 'Dismissed')
                  }}
                >
                  <X className="size-4 text-destructive" />
                </Button>
              ) : null}
            </div>
          )
        },
      },
    ]
  }, [categories, canManageItems, confirmItem, dismissItem, onOpenAttachment, toast])

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={emails}
          loading={loading}
          onRowClick={(e) => onSelectEmail(e.id)}
          empty={
            <EmptyState
              icon={Inbox}
              title="No emails here"
              description={
                hasAccounts
                  ? 'No emails in this category yet. Run a sync or import to pull more mail.'
                  : 'Connect a mailbox first, then import or sync to see emails here.'
              }
            />
          }
        />
      </CardContent>
    </Card>
  )
}
